import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SectionDecor } from "@/components/SectionDecor";
import { SiteFooter } from "@/components/SiteFooter";
import { CTABlock } from "@/components/CTABlock";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { HeroBackground } from "@/components/HeroBackground";
import faqHero from "@/assets/mckay/roofing-service.webp.asset.json";

const faqs = [
  { q: "What roofing services do you offer?", a: "Full-service roofing – repairs, replacements, emergency tarps, inspections and annual maintenance. Shingle, tile, metal and flat/low-slope systems for both homes and commercial buildings across Southern California." },
  { q: "How much does a new roof cost in Southern California?", a: "Most single-family shingle re-roofs land between $12,000 and $28,000 depending on size, pitch and tear-off condition. Tile and metal run higher. We give free written estimates after a real on-site inspection." },
  { q: "How long does a solar installation take?", a: "Most residential systems are installed in 1–3 days on-site. The full project – design, permits, HOA (if any), install and utility permission to operate – usually runs 6–10 weeks start to finish." },
  { q: "Do you handle solar and roofing together?", a: "Yes – that's a big reason people call us. If your roof is near the end of its life, we'll replace it and install solar as one coordinated project so panels aren't removed and reset a few years later." },
  { q: "Will solar actually save me money in Southern California?", a: "For most Southern California utility customers, yes – especially with NEM 3.0 and a properly sized battery. We model your actual usage against your rate plan before quoting, so you see real numbers, not a sales estimate." },
  { q: "How long does a roof last in Southern California?", a: "Asphalt shingle 20–30 years, concrete or clay tile 40–50+, metal 40–60. Coastal homes see faster wear from salt air; inland/east county roofs see more heat stress. Annual maintenance adds years to any of them." },
  { q: "Do you install Tesla Powerwall?", a: "Yes – we're a certified Powerwall installer. Most homeowners pair one or two Powerwalls with a new solar system for backup power and NEM 3.0 optimization." },
  { q: "Is your crew licensed and insured?", a: "Yes – fully licensed California C-39 roofing and C-46 solar contractors, with full general liability and workers' comp. We send the COI to your HOA or property manager on request." },
  { q: "Do you do emergency roof repair?", a: "Yes. If you have an active leak or storm damage, we can usually tarp and stabilize within 24 hours, then come back for the permanent repair once the roof is dry." },
  { q: "Do you help with insurance claims?", a: "Yes. We document damage with photos and measurements, meet the adjuster on-site, and work directly with your carrier so you're not stuck translating between two industries." },
  { q: "Can you install EV chargers?", a: "Yes – Level 2 EV chargers, standalone or bundled with a solar/battery project. If you're already upgrading your electrical for solar, adding EV charging is a small incremental cost." },
  { q: "Do you offer financing?", a: "Yes, on both roofing and solar. We work with several lenders offering fixed-rate financing and $0-down options. We'll show you the numbers on both cash and financed so you can compare." },
  { q: "How quickly can you get out for a quote?", a: "Usually within 2–5 business days. For active leaks or storm damage, same-day is often possible. Just call or send us a message with a couple of photos." },
  { q: "What areas do you serve?", a: "All of Southern California – Carlsbad, Pine Valley, Winter Gardens, Encinitas, Southern California, Coronado, Poway, La Presa, Oceanside, Fallbrook, Vista, Rancho Bernardo, Ramona and Mission Valley." },
  { q: "What if I'm not happy with the result?", a: "We come back and make it right. Our workmanship warranty backs every job, and we don't consider it done until you're satisfied with the work." },
];

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "Roofing & Solar FAQ – McKay Roofing & Solar" },
      { name: "description", content: "Answers to common roofing and solar questions from Southern California homeowners: costs, timelines, warranties, insurance claims and how we work." },
      { name: "keywords", content: "roofing FAQ Southern California, solar FAQ Southern California, roof replacement cost, solar installation timeline, Tesla Powerwall installer, McKay Roofing & Solar" },
      { property: "og:title", content: "Roofing & Solar FAQ – McKay Roofing & Solar" },
      { property: "og:description", content: "Answers to the most common roofing and solar questions from homeowners across Southern California." },
      { property: "og:url", content: "https://mckay.steadyautogrowthdemo.com/faq" },
    ],
    links: [{ rel: "canonical", href: "https://mckay.steadyautogrowthdemo.com/faq" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: FAQPage,
});

function FAQPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <section className="relative overflow-hidden bg-gradient-hero text-primary-foreground">
          <HeroBackground src={faqHero.url} alt="McKay Roofing & Solar technician walking a homeowner through a project" />
          <div className="relative mx-auto flex min-h-[85vh] max-w-4xl flex-col justify-center px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-28">
            <h1 className="text-5xl font-extrabold sm:text-6xl text-balance">Roofing &amp; solar questions, answered.</h1>
            <p className="mt-5 max-w-2xl text-lg text-primary-foreground/85">If you've got a question we haven't answered here, just <Link to="/contact" className="underline hover:text-accent">send it our way</Link>.</p>
          </div>
        </section>

        <section className="relative overflow-hidden">
          <SectionDecor variant="roof" side="right" tone="primary" opacity={0.05} />
          <div className="relative z-10 mx-auto max-w-3xl px-4 py-14 sm:px-6 lg:px-8">
          <Accordion type="single" collapsible>
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`q${i}`}>
                <AccordionTrigger className="text-left text-base font-semibold">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          </div>
        </section>

        <CTABlock />
      </main>
      <SiteFooter />
    </div>
  );
}
